import {
  Button,
  Flex,
  Grid,
  Loader,
  ScrollView,
  SearchField,
  Text,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CDropdown,
  CDropdownDivider,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import {
  equipmentByBranchID,
  equipmentByCompanyID,
  equipmentParametersByEquipmentID,
  listEquipment,
  sparesByEquipmentID,
} from "graphql/queries";
import {
  updateEquipment,
  updateEquipmentParameter,
  updateSpare,
} from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import { EquipmentCreateForm, EquipmentUpdateForm } from ".";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import {
  getBranchName,
  getCompanyName,
  getDeviceName,
} from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import EquipmentParameters from "./parameters/EquipmentParameters";
import EquipmentDocuments from "./documents/EquipmentDocuments";
import ActionIcon from "components/actions/ActionIcon";
import EquipmentInfoContainer from "./EquipmentInfoContainer";
import EquipmentsUpload from "./EquipmentsUpload";
import EquipmentPhotos from "./EquipmentPhotos";
import {
  Delete,
  Edit,
  Folder,
  Handyman,
  PhotoLibrary,
  Tune,
  Visibility,
} from "@mui/icons-material";
import { ServiceOrderCreateForm } from "modules/serviceOrders";
import ExecutedServiceOrderContainer from "modules/serviceOrders/ExecutedServiceOrder/ExecutedServiceOrderContainer";
import { convertToSlug } from "components/utils/functions";

const Equipments = () => {
  const { me } = useContext(LayoutContext);
  const [
    canCreate,
    canUpdate,
    canDelete,
    canReadParams,
    canReadDocs,
    canReadPhotos,
    canCreateServiceOrder,
  ] = usePermissions([
    "equipment:create",
    "equipment:update",
    "equipment:delete",
    "equipment:read-param",
    "equipment:read-doc",
    "equipment:read-photo",
    "service-order:create",
  ]);
  const [equipments, setEquipments] = useState([]);
  const [equipmentsLoading, setEquipmentsLoading] = useState(false);
  const [equipmentDetails] = useState();
  const [keyword, setKeyword] = useState("");
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    equipmentId: null,
    equipmentName: null,
  });
  const [modalEquipment, setModalEquipment] = useState({
    visible: false,
    equipmentId: null,
  });
  const [modalParams, setModalParams] = useState({
    visible: false,
    equipmentId: null,
  });
  const [modalDocs, setModalDocs] = useState({
    visible: false,
    equipmentId: null,
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      equipments.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        branchName: item.branchName || <Loader />,
        deviceName: !item.hasOwnProperty("deviceName") ? (
          <Loader />
        ) : (
          item.deviceName
        ),
        actions: (
          <>
            <CDropdown variant="dropdown" style={{ textAlign: "right" }}>
              <CDropdownToggle
                placement="bottom-end"
                className="py-0 pe-0 border-0"
                caret={false}
              >
                <ActionIcon
                  name={strings.equipments.moreOptions}
                  icon="MoreVert"
                  color="action"
                />
              </CDropdownToggle>
              <CDropdownMenu className="pt-0" placement="bottom-end">
                <CDropdownItem
                  role="button"
                  className="d-inline-flex align-items-center mt-1"
                  onClick={() => openModal("export", item.id)}
                >
                  <Visibility
                    fontSize="small"
                    color="action"
                    className="me-1"
                  />{" "}
                  {strings.equipments.details}
                </CDropdownItem>
                {canUpdate && (
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center mt-1"
                    onClick={() => openModal("update", item.id)}
                  >
                    <Edit fontSize="small" color="action" className="me-1" />{" "}
                    {strings.equipments.edit}
                  </CDropdownItem>
                )}
                {canReadParams && (
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center mt-1"
                    onClick={() => {
                      setModalParams({
                        visible: true,
                        equipmentId: item.id,
                        equipmentName: item.name,
                      });
                    }}
                  >
                    <Tune fontSize="small" color="action" className="me-1" />{" "}
                    {strings.equipments.params}
                  </CDropdownItem>
                )}
                {canReadDocs && (
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center mt-1"
                    onClick={() => {
                      setModalDocs({
                        visible: true,
                        equipmentId: item.id,
                        equipmentName: item.name,
                      });
                    }}
                  >
                    <Folder fontSize="small" color="action" className="me-1" />{" "}
                    {strings.equipments.docs}
                  </CDropdownItem>
                )}
                {canReadPhotos && (
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center mt-1"
                    onClick={() => openModal("read-photo", item.id)}
                  >
                    <PhotoLibrary
                      fontSize="small"
                      color="action"
                      className="me-1"
                    />{" "}
                    {strings.equipments.readPhotos}
                  </CDropdownItem>
                )}
                {canCreateServiceOrder && (
                  <CDropdownItem
                    role="button"
                    className="d-inline-flex align-items-center mt-1"
                    onClick={() => openModal("create-order", item.id)}
                  >
                    <Handyman
                      fontSize="small"
                      color="action"
                      className="me-1"
                    />{" "}
                    {strings.equipments.createServiceOrder}
                  </CDropdownItem>
                )}
                {canDelete && (
                  <>
                    <CDropdownDivider />
                    <CDropdownItem
                      role="button"
                      className="d-inline-flex align-items-center"
                      onClick={() => {
                        setModalDetails({
                          visible: true,
                          equipmentId: item.id,
                          equipmentName: item.name,
                        });
                      }}
                    >
                      <Delete fontSize="small" color="error" className="me-1" />{" "}
                      <Text variation="error">{strings.equipments.delete}</Text>
                    </CDropdownItem>
                  </>
                )}
              </CDropdownMenu>
            </CDropdown>
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [equipments, equipmentDetails?.id]
  );

  const onNext = () => {
    fetchEquipments(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchEquipments(previous, false);
  };
  const onKeywordSubmit = (keywordValue) => {
    const keywordSplit = keywordValue && keywordValue.split(" ");
    setPaginationTokens({
      previous: [],
      nextToken: [],
    });
    setKeyword(keywordSplit);
  };
  const openModal = (action, id) =>
    setModalEquipment({ visible: true, action, equipmentId: id });
  const onSuccessEquipment = () => {
    setModalEquipment({ visible: false });
    fetchEquipments();
  };
  const onSuccessServiceOrder = (id) => {
    setModalEquipment({
      visible: true,
      action: "execute-order",
      serviceOrderId: id,
    });
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  async function fetchBranchName(id) {
    try {
      const branchData = await API.graphql(
        graphqlOperation(getBranchName, {
          id,
        })
      );
      const branchItem = branchData.data.getBranch;
      return branchItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchBranchName", err);
      return "";
    }
  }

  async function fetchDeviceName(id) {
    try {
      const deviceData = await API.graphql(
        graphqlOperation(getDeviceName, {
          id,
        })
      );
      const deviceItem = deviceData.data.getDevice;
      return deviceItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchDeviceName", err);
      return "";
    }
  }

  const getNamesByEquipments = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);
      const branchName = await fetchBranchName(item.branchID);
      const deviceName = item.deviceID
        ? await fetchDeviceName(item.deviceID)
        : "";

      setEquipments((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        updatedList[index] = {
          ...updatedList[index],
          companyName,
          branchName,
          deviceName,
        };

        return updatedList;
      });
    });
  };

  async function fetchEquipments(token, isNext = true, limit = 20) {
    let objOperation = {};
    let nameOperation = listEquipment;
    let nameOperationStr = "listEquipment";
    try {
      setEquipmentsLoading(true);

      if (!!searchBy.branch.id) {
        objOperation = {
          branchID: searchBy.branch.id,
        };
        nameOperation = equipmentByBranchID;
        nameOperationStr = "equipmentByBranchID";
      } else if (!!searchBy.company.id || !!me.companyID) {
        objOperation = {
          companyID: searchBy.company.id || me.companyID,
        };
        nameOperation = equipmentByCompanyID;
        nameOperationStr = "equipmentByCompanyID";
      } else return;

      let accumulated = [];
      let nextTokenTemp = token ?? null;

      const MAX_PAGE_SIZE = 20;

      do {
        const remaining = Math.max(limit - accumulated.length, 0);
        if (remaining <= 0) break;

        const pageLimit = Math.min(remaining, MAX_PAGE_SIZE);

        const equipmentsData = await API.graphql(
          graphqlOperation(nameOperation, {
            ...objOperation,
            limit: pageLimit,
            nextToken: nextTokenTemp,
            filter: keyword
              ? {
                  and: keyword.map((key) => ({
                    or: [
                      { name: { contains: key } },
                      { description: { contains: key } },
                      { slug: { contains: convertToSlug(key) } },
                    ],
                  })),
                  deletedAt: { attributeExists: false },
                }
              : { deletedAt: { attributeExists: false } },
          })
        );

        const pageItems = equipmentsData?.data?.[nameOperationStr]?.items ?? [];
        const responseNextToken =
          equipmentsData?.data?.[nameOperationStr]?.nextToken ?? null;

        accumulated = [...accumulated, ...pageItems];

        nextTokenTemp = responseNextToken;
      } while (nextTokenTemp && accumulated.length < limit);

      setEquipments(accumulated);
      getNamesByEquipments(accumulated);

      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: nextTokenTemp,
        };
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setEquipmentsLoading(false);
    } finally {
      setEquipmentsLoading(false);
    }
  }

  async function equipmentParametersDelete(equipmentID) {
    try {
      const results = await API.graphql(
        graphqlOperation(equipmentParametersByEquipmentID, {
          equipmentID,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete =
        results.data.equipmentParametersByEquipmentID.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(updateEquipmentParameter, {
              input: { id: res.id, deletedAt: new Date().toISOString() },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones", err);
    }
  }

  async function equipmentSparesDelete(equipmentID) {
    try {
      const results = await API.graphql(
        graphqlOperation(sparesByEquipmentID, {
          equipmentID,
          filter: { deletedAt: { attributeExists: false } },
        })
      );
      const resultsToDelete = results.data.sparesByEquipmentID.items;
      await Promise.all(
        resultsToDelete?.map((res) =>
          API.graphql(
            graphqlOperation(updateSpare, {
              input: { id: res.id, equipmentID: null },
            })
          )
        )
      );
    } catch (err) {
      console.log("Error al eliminar relaciones con repuestos", err);
    }
  }

  async function equipmentDelete(id) {
    try {
      await equipmentParametersDelete(id);
      await equipmentSparesDelete(id);

      const result = await API.graphql(
        graphqlOperation(updateEquipment, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateEquipment.id;
      setEquipments(equipments.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        equipmentId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchEquipments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id, keyword]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "15%" },
    },
    {
      key: "branchName",
      label: strings.table.headers.branchName,
      _style: { width: "15%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "35%" },
    },
    // {
    //   key: "location",
    //   label: strings.table.headers.location,
    //   _style: { width: "15%" },
    // },
    {
      key: "deviceName",
      label: strings.table.headers.deviceName,
      _style: { width: "30%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "5%" },
    },
  ];

  return (
    <div>
      <Flex justifyContent="space-between">
        <span className="fs-3">{strings.title}</span>
        <Flex style={styles.buttonAdd}>
          {canCreate && (
            <>
              {me.companyID && (
                <Button
                  className=""
                  children={strings.equipments.massiveUpload}
                  variation="primary"
                  onClick={() => openModal("upload")}
                />
              )}
              <Button
                children={strings.equipments.addEquipment}
                variation="primary"
                onClick={() => openModal("create")}
              />
            </>
          )}
        </Flex>
      </Flex>
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter searchBy={searchBy} setSearchBy={setSearchBy} />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {equipmentsLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              // responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!equipmentDetails?.id} style={styles.collapseCard}>
          <CCard key={equipmentDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{equipmentDetails?.name}"
              </h4>
              <p className="text-muted">
                <b>{strings.details.description}:</b>{" "}
                {equipmentDetails?.description}
              </p>
              <p className="text-muted">
                <b>{strings.details.location}:</b> {equipmentDetails?.location}
              </p>
              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {moment(equipmentDetails?.createdAt).format(
                  "YYYY-MM-DD HH:mm:ss"
                )}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.equipmentName,
        })}
        onCancel={() => setModalDetails({ visible: false, equipmentId: null })}
        onConfirm={() => equipmentDelete(modalDetails.equipmentId)}
      />
      {modalEquipment.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalEquipment.visible}
          onClose={() => setModalEquipment({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalEquipment.action === "create"
                ? strings.modalEquipment.addTitle
                : modalEquipment.action === "export"
                ? strings.modalEquipment.exportTitle
                : modalEquipment.action === "upload"
                ? strings.modalEquipment.uploadTitle
                : modalEquipment.action === "read-photo"
                ? strings.modalEquipment.readPhotosTitle
                : modalEquipment.action === "create-order"
                ? strings.modalEquipment.createServiceOrderTitle
                : modalEquipment.action === "execute-order"
                ? strings.modalEquipment.executeServiceOrderTitle
                : strings.modalEquipment.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalEquipment.action === "create" ? (
                <EquipmentCreateForm onSuccess={onSuccessEquipment} />
              ) : modalEquipment.action === "export" ? (
                <EquipmentInfoContainer
                  equipmentID={modalEquipment.equipmentId}
                  onSuccess={onSuccessEquipment}
                />
              ) : modalEquipment.action === "upload" ? (
                <EquipmentsUpload onSuccess={onSuccessEquipment} />
              ) : modalEquipment.action === "read-photo" ? (
                <EquipmentPhotos equipmentID={modalEquipment.equipmentId} />
              ) : modalEquipment.action === "create-order" ? (
                <ServiceOrderCreateForm
                  onSuccess={onSuccessServiceOrder}
                  equipmentID={modalEquipment.equipmentId}
                />
              ) : modalEquipment.action === "execute-order" ? (
                <ExecutedServiceOrderContainer
                  serviceOrderId={modalEquipment.serviceOrderId}
                  onSuccess={onSuccessEquipment}
                />
              ) : (
                <EquipmentUpdateForm
                  equipmentId={modalEquipment.equipmentId}
                  onSuccess={onSuccessEquipment}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
      {modalParams.visible && (
        <EquipmentParameters
          equipmentID={modalParams.equipmentId}
          equipmentName={modalParams.equipmentName}
          visible={modalParams.visible}
          onClose={() => setModalParams({ visible: false })}
        />
      )}
      {modalDocs.visible && (
        <EquipmentDocuments
          equipmentID={modalDocs.equipmentId}
          equipmentName={modalDocs.equipmentName}
          visible={modalDocs.visible}
          onClose={() => setModalDocs({ visible: false })}
        />
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Equipos",
    successDeleted: "Equipo eliminado con éxito",
    equipments: {
      massiveUpload: "Carga Masiva",
      downloadFormat: "Descargar formato CSV",
      addEquipment: "Agregar Equipo",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      params: "Parámetros",
      docs: "Documentos",
      export: "Exportar Hoja de Vida",
      details: "Ver Hoja de Vida",
      readPhotos: "Ver Fotos",
      moreOptions: "Más Opciones",
      createServiceOrder: "Crear Orden de Servicio",
    },
    responses: {
      errorFetch: "Error al consultar equipo",
      unexpectedError: "Ha ocurrido un error inesperado",
      successUpload: "Se ha cargado la información con éxito",
    },
    table: {
      headers: {
        name: "Nombre",
        location: "Ubicación",
        companyName: "Empresa",
        branchName: "Sede",
        deviceName: "Dispositivo",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Equipo",
      description: "Descripción",
      location: "Ubicación",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el equipo {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalEquipment: {
      addTitle: "Agregar Equipo",
      updateTitle: "Actualizar Equipo",
      exportTitle: "Ver Hoja de Vida del Equipo",
      uploadTitle: "Carga Masiva",
      readPhotosTitle: "Fotos del Equipo",
      createServiceOrderTitle: "Crear Orden de Servicio",
      executeServiceOrderTitle: "Ejecutar Orden de Servicio",
    },
  },
});

const styles = {
  buttonAdd: { justifyContent: "end", flexWrap: "wrap" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Equipments;
